import axios, { type AxiosInstance } from "axios"
import { jwtDecode } from "jwt-decode"
import { clearActiveExhibition } from "../utils/exhibitionContext"

interface DecodedToken {
  exp: number
  user_id: number
  email: string
  is_superuser: boolean
  [key: string]: any
}

interface SignupData {
  first_name: string
  last_name: string
  email: string
  phone?: string
  token?: string
}

const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://127.0.0.1:8000",
  headers: { "Content-Type": "application/json" },
})

const AuthService = {
  // Set token in localStorage + axios headers
  setAuthHeader(token: string) {
    console.log("🔐 AuthService: Setting auth header")
    localStorage.setItem("accessToken", token)
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`
  },

  getToken() {
    const token = localStorage.getItem("accessToken") || ""
    console.log("🔐 AuthService: Getting token, exists:", !!token)
    return token
  },

  getDecodedToken(): DecodedToken | null {
    const token = this.getToken()
    if (!token) {
      console.log("🔐 AuthService: No token to decode")
      return null
    }

    try {
      const decoded: DecodedToken = jwtDecode(token)
      console.log("🔐 AuthService: Token decoded successfully:", {
        user_id: decoded.user_id,
        email: decoded.email,
        exp: new Date(decoded.exp * 1000).toISOString(),
      })
      return decoded
    } catch (error) {
      console.error("🔐 AuthService: Token decode failed:", error)
      return null
    }
  },

  isTokenValid(): boolean {
    const decoded = this.getDecodedToken()
    if (!decoded || !decoded.exp) {
      console.log("🔐 AuthService: Token invalid - no decoded data or exp")
      return false
    }

    const isValid = decoded.exp * 1000 > Date.now()
    console.log("🔐 AuthService: Token validity check:", {
      expires: new Date(decoded.exp * 1000).toISOString(),
      now: new Date().toISOString(),
      isValid,
    })
    return isValid
  },

  // ✅ Check if user has valid session
  hasValidSession(): boolean {
    try {
      console.log("🔐 AuthService: Checking session validity...")

      const token = this.getToken()
      if (!token) {
        console.log("🔐 AuthService: No token found")
        return false
      }

      const decoded = this.getDecodedToken()
      if (!decoded) {
        console.log("🔐 AuthService: Could not decode token")
        return false
      }

      // Check if token is not expired
      const isValid = decoded.exp * 1000 > Date.now()
      console.log("🔐 AuthService: Session validity result:", isValid)
      return isValid
    } catch (error) {
      console.error("🔐 AuthService: Session validation error:", error)
      return false
    }
  },

  // ✅ Clear all session data including the active exhibition context
  logout() {
    console.log("🔐 AuthService: Logging out and clearing session data")
    localStorage.removeItem("accessToken")
    localStorage.removeItem("refreshToken")
    localStorage.removeItem("user")
    // Clear exhibition context so the next user starts fresh
    clearActiveExhibition()
    sessionStorage.clear()
    delete api.defaults.headers.common["Authorization"]
  },

  // Enhanced signup method to support both regular and focal person signup
  async signup(data: SignupData) {
    const response = await api.post("/api/accounts/auth/signup/", data)
    return response.data
  },

  verifyEmail(token: string, password: string) {
    return api.post("/api/accounts/auth/verify-email/", { token, password })
  },

  async loginStep1(email: string, password: string) {
    console.log("🔐 AuthService: Login step 1 for:", email)
    const res = await api.post("/api/accounts/auth/login/", { email, password })
    if (res.data?.access) {
      console.log("🔐 AuthService: Login step 1 successful, setting token")
      this.setAuthHeader(res.data.access)
    }
    return res.data
  },

  async loginStep2(email: string, otp: string) {
    console.log("🔐 AuthService: Login step 2 for:", email)
    const res = await api.post("/api/accounts/auth/verify-otp/", { email, otp })
    const { access, refresh } = res.data
    console.log("🔐 AuthService: Login step 2 successful, setting tokens")
    this.setAuthHeader(access)
    localStorage.setItem("refreshToken", refresh)
    return res.data
  },

  requestPasswordReset(email: string) {
    return api.post("/forgot-password/", { email })
  },

  resetPassword(token: string, newPassword: string) {
    return api.post("/reset-password/", { token, password: newPassword })
  },
}

export default AuthService